import { ColumnSort } from "@tanstack/react-table";

import {
  CreateStaffType,
  EdgeFunctionsEnum,
  UpdateStaffType,
} from "@libs/schemas";
import { DEFAULT_PAGE_SIZE } from "@/domain/constants";
import { StaffFilterType } from "@/domain/staff";
import { sendSentryError } from "@/services/sentry";
import { invokeEdgeFunction, supabaseClient } from "@/services/supabase";
import { paginationRange, parseSearchTerm } from "@/utils/helpers";

export const StaffApi = {
  getStaff: async (staffId?: string | null) => {
    if (!staffId) {
      return;
    }

    const { data, error } = await supabaseClient
      .from("staff")
      .select("*, staff_campuses ( campus: campuses (id, name, short_code))")
      .eq("id", staffId)
      .single();

    if (error) {
      sendSentryError({ message: "Error getting staff", error });
    }

    if (data)
      return {
        ...data,
        campuses: data.staff_campuses.map(item => item.campus),
      };

    return null;
  },

  listStaff: async ({
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort,
    filters,
    needsPagination = true,
  }: {
    page: number;
    pageSize?: number;
    sort?: ColumnSort;
    filters?: StaffFilterType;
    needsPagination?: boolean;
  }) => {
    const range = paginationRange(page, pageSize);

    const query = supabaseClient
      .from("staff")
      .select(
        "*, staff_campuses!inner(campus: campuses(id, name, short_code, province))",
      );

    if (filters?.campusesIds?.length) {
      query.in("staff_campuses.campus_id", filters.campusesIds);
    }

    if (filters?.search?.length) {
      query.textSearch("full_text_search", parseSearchTerm(filters.search));
    }
    if (filters?.roles?.length) {
      query.in("role", filters.roles);
    }
    if (filters?.statuses?.length) {
      query.in("status", filters.statuses);
    }

    if (sort) {
      if (sort.id === "fullname") {
        query.order("first_name", { ascending: !sort.desc });
        query.order("last_name", { ascending: !sort.desc });
      } else {
        query.order(sort.id, { ascending: !sort.desc });
      }
    } else {
      query.order("created_at", { ascending: true });
    }

    if (needsPagination) {
      query.range(range.from, range.to);
    }

    const { data, error } = await query;
    if (error) {
      sendSentryError({ message: "Error getting staff list", error });
    }

    if (data) {
      return data.map(staff => ({
        ...staff,
        campuses: staff.staff_campuses.map(item => item.campus),
      }));
    }

    return data ?? [];
  },

  updateStaff: async ({
    staffId,
    params,
  }: {
    staffId: string;
    params: Omit<UpdateStaffType, "id">;
  }) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.UpdateStaff,
      opts: {
        body: { ...params, id: staffId },
      },
    });

    if (error || !data) {
      const errorMessage = error?.message;

      sendSentryError({
        message: "Error updating staff",
        error: errorMessage,
      });
    }

    return data;
  },

  countStaff: async ({
    filters,
    campusesIds,
  }: {
    filters?: StaffFilterType;
    campusesIds?: string[];
  }) => {
    const query = supabaseClient
      .from("staff")
      .select("id, staff_campuses!inner(campus_id)", { count: "exact" });

    if (campusesIds?.length) {
      query.in("staff_campuses.campus_id", campusesIds);
    }
    if (filters?.search?.length) {
      query.textSearch("full_text_search", parseSearchTerm(filters.search));
    }
    if (filters?.roles?.length) {
      query.in("role", filters.roles);
    }
    if (filters?.statuses?.length) {
      query.in("status", filters.statuses);
    }

    const { count, error } = await query;

    if (error) {
      sendSentryError({ message: "Error counting staff", error });
      throw error;
    }

    return count ?? 0;
  },

  createStaff: async (params: CreateStaffType) => {
    const { data, error } = await invokeEdgeFunction({
      method: "POST",
      func: EdgeFunctionsEnum.CreateStaff,
      opts: {
        body: params,
      },
    });

    if (error || !data) {
      const errorMessage = error?.message;

      sendSentryError({
        message: "Error creating staff",
        error: errorMessage,
      });
    }

    return data;
  },
};
